
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { secureLog } from "@/utils/securityUtils";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    // Check if we have the reset token in the URL
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const type = hashParams.get('type');

    const establishSession = async () => {
      if (type === 'recovery' && accessToken && refreshToken) {
        // Set the session with the tokens
        await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        secureLog.info('Password reset session established via URL tokens');
        return;
      }

      // Fallback: if no URL tokens, allow reset when a valid session already exists
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        secureLog.info('Password reset using existing session');
        return;
      }

      // No valid token or session, redirect to forgot password
      secureLog.warn('Invalid or expired reset link, or no active session');
      toast.error("Ungültiger oder abgelaufener Reset-Link");
      navigate("/forgot-password");
    };

    establishSession();
  }, [navigate]);

  const validateForm = () => {
    if (password !== confirmPassword) {
      setPasswordError("Die Passwörter stimmen nicht überein");
      return false;
    }
    
    if (password.length < 6) {
      setPasswordError("Das Passwort muss mindestens 6 Zeichen lang sein");
      return false;
    }
    
    setPasswordError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });
      
      if (error) {
        secureLog.error('Password update error:', error.message);
        toast.error(error.message);
        throw error;
      }
      
      secureLog.info('Password updated successfully');
      toast.success("Passwort erfolgreich geändert!");
      navigate("/timeline");
    } catch (error) {
      secureLog.error("Password update error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-uplivion-lightBlue/30 flex flex-col items-center justify-center p-4">
      <Link to="/" className="text-3xl font-bold text-uplivion-blue mb-8">
        uplivion
      </Link>
      
      <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8">
        <h1 className="text-2xl font-bold text-center mb-6">Neues Passwort erstellen</h1>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">Neues Passwort</Label>
            <Input 
              id="password" 
              type="password" 
              placeholder="••••••••" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
            <Input 
              id="confirmPassword" 
              type="password" 
              placeholder="••••••••" 
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required 
            />
            {passwordError && (
              <p className="text-sm text-red-500 mt-1">{passwordError}</p>
            )}
          </div>
          
          <Button 
            type="submit" 
            className="w-full bg-uplivion-blue hover:bg-blue-700"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Lädt...
              </>
            ) : (
              "Passwort ändern"
            )}
          </Button>
        </form>
        
        <div className="mt-6 text-center">
          <Link 
            to="/login" 
            className="text-uplivion-blue hover:underline"
          >
            Zurück zur Anmeldung
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
